import React, { useState } from 'react';
import {
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Grid,
  Button,
} from '@material-ui/core';
import {
  ChevronLeft,
  ChevronRight,
  CalendarToday,
  Home,
} from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { format, addDays, startOfWeek, endOfWeek, isSameDay } from 'date-fns';
import moment from 'moment';
import InfiniteCalendar from 'react-infinite-calendar';
import { localesMap } from '../OperationHoursTab/OperationHoursCalendar';
import { useStyles } from './styles';
import './../Fields/Calendar/styles/infiniteCalendarStyles.css';
import './../Fields/Calendar/styles/infiniteCustomStyles.css';

interface WeekSelectorProps {
  selectedWeek: Date;
  onWeekChange: (week: Date) => void;
  showTodayButton?: boolean;
  className?: string;
}

function WeekSelector({
  selectedWeek,
  onWeekChange,
  showTodayButton = true,
  className,
}: WeekSelectorProps) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);
  const [calendarSelectedDate, setCalendarSelectedDate] = useState<
    string | null
  >(null);

  const handleWeekChange = (direction: 'prev' | 'next') => {
    const newWeek = addDays(selectedWeek, direction === 'next' ? 7 : -7);
    onWeekChange(newWeek);
  };

  const handleTodayClick = () => {
    onWeekChange(new Date());
  };

  const handleCalendarClick = () => {
    setIsCalendarOpen(true);
  };

  const handleCalendarClose = () => {
    setIsCalendarOpen(false);
  };

  const handleWeekSelection = (
    startDate: moment.Moment,
    endDate: moment.Moment,
  ) => {
    onWeekChange(startDate.toDate());
    setIsCalendarOpen(false);
  };

  const isCurrentWeek = () => {
    const today = new Date();
    const currentWeekStart = startOfWeek(today, { weekStartsOn: 1 });
    const selectedWeekStart = startOfWeek(selectedWeek, { weekStartsOn: 1 });

    return isSameDay(currentWeekStart, selectedWeekStart);
  };

  const formatWeekRange = () => {
    const startOfWeekDate = startOfWeek(selectedWeek, { weekStartsOn: 1 });
    const endOfWeekDate = endOfWeek(selectedWeek, { weekStartsOn: 1 });

    const startMonth = format(startOfWeekDate, 'MMM');
    const startDay = format(startOfWeekDate, 'd');
    const endMonth = format(endOfWeekDate, 'MMM');
    const endDay = format(endOfWeekDate, 'd');
    const year = format(startOfWeekDate, 'yyyy');

    return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
  };

  return (
    <>
      <div className={`${classes.weekSelector} ${className || ''}`}>
        <IconButton
          size="small"
          onClick={() => handleWeekChange('prev')}
          className={classes.iconButton}
        >
          <ChevronLeft />
        </IconButton>
        <IconButton
          size="small"
          onClick={handleCalendarClick}
          className={classes.iconButton}
        >
          <CalendarToday fontSize="small" />
        </IconButton>
        <Typography
          variant="body2"
          className={classes.weekText}
          onClick={handleCalendarClick}
        >
          {t('operationHours.week')} {formatWeekRange()}
        </Typography>
        <IconButton
          size="small"
          onClick={() => handleWeekChange('next')}
          className={classes.iconButton}
        >
          <ChevronRight />
        </IconButton>
        {showTodayButton && !isCurrentWeek() && (
          <IconButton
            size="small"
            onClick={handleTodayClick}
            className={classes.iconButton}
            title={t('operationHours.goToCurrentWeek')}
          >
            <Home />
          </IconButton>
        )}
      </div>

      {/* Calendar Modal */}
      <Dialog
        open={isCalendarOpen}
        onClose={handleCalendarClose}
        maxWidth="sm"
        PaperProps={{
          className: classes.calendarDialog,
        }}
      >
        <DialogTitle>
          {t('operationHours.selectWeek')}
          <Typography variant="body2" className={classes.calendarDialogTitle}>
            {t(
              'operationHours.selectWeekInstruction',
              'Click on any day to select that entire week (Monday to Sunday)',
            )}
          </Typography>
        </DialogTitle>
        <DialogContent className={classes.calendarDialogContent}>
          <Paper className={classes.calendarPaper}>
            <Grid item xs={12}>
              <InfiniteCalendar
                locale={{
                  headerFormat: '',
                  locale: localesMap['en'],
                  blank: t('app.common.calendar.selectADate'),
                  todayLabel: {
                    long: t('app.common.calendar.today', 'today'),
                    short: t('app.common.calendar.today', 'Today'),
                  },
                  weekdays: [
                    t('app.common.calendar.monday_short', 'mon'),
                    t('app.common.calendar.tuesday_short', 'tue'),
                    t('app.common.calendar.wednesday_short', 'wed'),
                    t('app.common.calendar.thursday_short', 'thu'),
                    t('app.common.calendar.friday_short', 'fri'),
                    t('app.common.calendar.saturday_short', 'sat'),
                    t('app.common.calendar.sunday_short', 'sun'),
                  ],
                  weekStartsOn: 0,
                }}
                theme={{
                  selectionColor: '#1976d2',
                  headerColor: 'transparent',
                  weekdayColor: 'rgb(216, 216, 216)',
                }}
                width={'100%'}
                height={400}
                rowHeight={50}
                selected={calendarSelectedDate || moment().format('YYYY-MM-DD')}
                displayDate={selectedWeek}
                onSelect={selectedDate => {
                  if (selectedDate && moment(selectedDate).isValid()) {
                    setCalendarSelectedDate(
                      moment(selectedDate).format('YYYY-MM-DD'),
                    );

                    const clickedDate = moment(selectedDate).startOf('day');
                    const startOfWeek = clickedDate.clone().startOf('isoWeek');
                    const endOfWeek = startOfWeek.clone().add(6, 'days');

                    handleWeekSelection(startOfWeek, endOfWeek);
                  }
                }}
              />
            </Grid>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCalendarClose} color="primary">
            {t('app.common.cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default WeekSelector;
